import { ArrowLeft, Ban, CalendarDays, Download, LoaderCircle, LockKeyhole, Mail, Paperclip, ShieldAlert } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { CustomerGate } from "../../components/AuthGate";
import { Avatar } from "../../components/Avatar";
import { Composer } from "../../components/Composer";
import { MessageAttachments } from "../../components/MessageAttachments";
import { MessageBody } from "../../components/MessageBody";
import { MessageCc } from "../../components/MessageCc";
import { TicketSubmittedNotice } from "../../components/TicketSubmittedNotice";
import { PageError, PageLoader } from "../../components/StateViews";
import { PriorityBadge, StatusBadge } from "../../components/StatusBadge";
import { useSession } from "../../context/SessionContext";
import { useToast } from "../../context/ToastContext";
import { useAsync } from "../../hooks/useAsync";
import { createSubmissionSignature, useSubmissionAttempt } from "../../hooks/useSubmissionAttempt";
import { useTicketRealtime } from "../../hooks/useTicketRealtime";
import { apiRequest, uploadAttachments } from "../../lib/api";
import { mergeLatestTicket } from "../../lib/mergeTicketMessages";
import { formatBytes, formatDate, formatRelative, ticketLabel } from "../../lib/format";
import type { Ticket } from "../../lib/types";
import { customerTicketPath } from "../../lib/ticketLinks";

function unwrapTicket(value: Ticket | { ticket: Ticket }) {
  return "ticket" in value ? value.ticket : value;
}

function CustomerTicketDetail() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { config, user } = useSession();
  const { showToast } = useToast();
  const [sending, setSending] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const inFlightRef = useRef(false);
  const refreshScopeRef = useRef({ id, sequence: 0 });
  if (refreshScopeRef.current.id !== id) refreshScopeRef.current = { id, sequence: 0 };
  const submission = useSubmissionAttempt(`customer-reply:${id}`);
  const result = useAsync(async (signal) => unwrapTicket(await apiRequest<Ticket | { ticket: Ticket }>(`/tickets/${encodeURIComponent(id)}`, { signal })), [id]);

  useEffect(() => {
    // Only canonicalize an authenticated, authorized response for this URL.
    // The ID match prevents stale data from redirecting to the previous ticket.
    if (!result.loading && result.data?.id === id && result.data.publicId && result.data.publicId !== id) {
      navigate(customerTicketPath(result.data) + location.search + location.hash, { replace: true });
    }
  }, [id, result.loading, result.data?.id, result.data?.publicId, location.search, location.hash, navigate]);

  async function refreshLatestMessages() {
    const scope = refreshScopeRef.current;
    if (scope.id !== id) return;
    const sequence = ++scope.sequence;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 20_000);
    try {
      const latest = unwrapTicket(await apiRequest<Ticket | { ticket: Ticket }>(`/tickets/${encodeURIComponent(id)}`, { signal: controller.signal }));
      result.setData((current) => refreshScopeRef.current === scope && sequence === scope.sequence ? mergeLatestTicket(current, latest) : current);
    } finally { window.clearTimeout(timeout); }
  }

  useTicketRealtime(result.loading ? "" : result.data?.id ?? "", refreshLatestMessages);

  async function sendReply(input: { body: string; html?: string; files: File[]; cc: string[] }) {
    if (inFlightRef.current) return;
    const draft = { body: input.body.trim(), html: input.html, visibility: "public" as const, cc: input.cc };
    const attemptKey = submission.keyFor(createSubmissionSignature(draft, input.files));
    inFlightRef.current = true;
    setSending(true);
    try {
      const attachments = await uploadAttachments(input.files, attemptKey);
      await apiRequest(`/tickets/${encodeURIComponent(id)}/messages`, {
        method: "POST",
        idempotencyKey: attemptKey,
        body: { ...draft, attachmentIds: attachments.map((attachment) => attachment.id) },
      });
      await refreshLatestMessages();
      submission.reset(attemptKey);
      showToast("Reply sent.", "success");
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Reply could not be sent.", "error");
      throw requestError;
    } finally {
      inFlightRef.current = false;
      setSending(false);
    }
  }

  async function loadOlderMessages() {
    const cursor = result.data?.messagePage?.nextCursor;
    if (!cursor || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const older = unwrapTicket(await apiRequest<Ticket | { ticket: Ticket }>(
        `/tickets/${encodeURIComponent(id)}?before=${encodeURIComponent(cursor)}`,
      ));
      result.setData((current) => {
        if (!current) return older;
        const currentIds = new Set((current.messages ?? []).map((message) => message.id));
        return {
          ...current,
          messages: [...(older.messages ?? []).filter((message) => !currentIds.has(message.id)), ...(current.messages ?? [])],
          messagePage: older.messagePage,
        };
      });
    } catch (requestError) {
      showToast(requestError instanceof Error ? requestError.message : "Older messages could not be loaded.", "error");
    } finally {
      setLoadingOlder(false);
    }
  }

  if (result.loading) return <PageLoader label="Loading ticket…" />;
  if (result.error || !result.data) return <PageError message={result.error ?? "Ticket not found."} onRetry={() => void result.refresh()} />;
  const ticket = result.data;

  return (
    <section className="ticket-detail-page">
      <Link className="back-link" to="/tickets"><ArrowLeft aria-hidden="true" size={16} /> My tickets</Link>
      <div className="ticket-detail-grid">
        <div className="conversation-column">
          <header className="ticket-title card">
            <div className="ticket-title__meta"><span>{ticketLabel(ticket.number)}</span><span>Updated {formatRelative(ticket.updatedAt)}</span></div>
            <h1>{ticket.subject}</h1>
            <div className="ticket-card__badges"><StatusBadge status={ticket.status} /><PriorityBadge priority={ticket.priority} /></div>
          </header>
          <TicketSubmittedNotice key={ticket.id} ticket={ticket} />
{ticket.quarantineType === "spam" && <div className="settings-callout settings-callout--warning ticket-spam-banner"><ShieldAlert aria-hidden="true" /><p><strong>This ticket is marked as spam</strong>Your ticket remains available here while the support team reviews it. Public updates will continue to appear in this conversation.</p></div>}
                    {ticket.messagePage?.hasMore && <div className="message-history-control"><button className="button button--secondary button--small" type="button" disabled={loadingOlder} onClick={() => void loadOlderMessages()}>{loadingOlder && <LoaderCircle className="spin" aria-hidden="true" size={14} />} {loadingOlder ? "Loading…" : "Load 10 older messages"}</button></div>}
          <div className="conversation" aria-label="Ticket conversation">
            {(ticket.messages ?? []).map((message) => (
              <article className={`message message--${message.author.type === "customer" ? "customer" : "agent"}`} key={message.id}>
                <Avatar name={message.author.name} url={message.author.avatarUrl} />
                <div className="message__content">
                  <header><strong>{message.author.name}</strong><span title={formatDate(message.createdAt)}>{formatRelative(message.createdAt)}</span></header>
                  <div className="message__routing"><span>{message.channel === "email" ? "Received by email" : message.channel === "api" ? "Sent through API" : message.channel === "chat" ? "Sent through chat" : "Sent through support portal"}</span></div>
                  <MessageBody body={message.body} html={message.bodyHtml} />
                  <MessageCc emails={message.cc} />
                                    {message.attachments?.length ? <MessageAttachments attachments={message.attachments} /> : null}
                </div>
              </article>
            ))}
          {ticket.status === "closed" && <div className="closed-notice card"><LockKeyhole aria-hidden="true" /><div><strong>This ticket is closed</strong><p>This conversation no longer accepts replies. If you need further help, please <Link to="/new">create a new ticket</Link>.</p></div></div>}
          {ticket.status !== "closed" && (user?.kind === "customer" && user.ticketCreationBlocked ? (
            <div className="restricted-reply-notice card"><Ban aria-hidden="true" /><div><strong>Replies are disabled for this account</strong><p>You can continue to read this ticket, but your account cannot create replies or upload attachments.</p></div></div>
          ) : (
            <Composer allowCc richText initialCc={ticket.collaborators?.map((item) => item.email)} busy={sending} maxAttachmentBytes={config.maxAttachmentBytes} onSend={sendReply} />
          ))}
          </div>
        </div>
        <aside className="ticket-sidebar">
          <section className="card"><h2>Ticket details</h2><dl className="detail-list"><div><dt>Status</dt><dd><StatusBadge status={ticket.status} /></dd></div><div><dt>Priority</dt><dd>{ticket.priority}</dd></div><div><dt>Created</dt><dd><CalendarDays aria-hidden="true" size={15} /> {formatDate(ticket.createdAt)}</dd></div>{ticket.mailbox?.address && <div><dt>Support email</dt><dd className="ticket-support-address"><Mail aria-hidden="true" size={15} /> <span>{ticket.mailbox.address}</span></dd></div>}{ticket.collaborators?.length ? <div><dt>Cc</dt><dd className="ticket-cc-list">{ticket.collaborators.map((item) => <span key={item.email}>{item.email}</span>)}</dd></div> : null}</dl></section>
          {ticket.relatedObjects?.length ? <section className="card"><h2>Related to</h2><div className="related-list">{ticket.relatedObjects.map((item) => <a key={`${item.type}:${item.id}`} href={item.url ?? "#"}><span>{item.type}</span><strong>{item.label ?? `#${item.id}`}</strong></a>)}</div></section> : null}
          <section className="privacy-note"><LockKeyhole aria-hidden="true" size={17} /><p>You, the listed Cc participants, and authorized Next-Cart support staff can see public conversation updates.</p></section>
        </aside>
      </div>
    </section>
  );
}

export function TicketDetailPage() {
  return <CustomerGate><CustomerTicketDetail /></CustomerGate>;
}
